import React, { useCallback, useMemo, useState } from "react";

import { Autocomplete, Button, Drawer, Stack, TextField, TextFieldProps, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { SimpleHead } from "components";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useDidUpdate } from "hooks";
import { useDateFormatSelect, useInvestorsSelector, useMeasureParametersSelector, useTaskDealsSelect } from "store";
import { DealBase, IInvestor } from "store/types";

import { AiDataFilters } from "../context/AiData-context";

export const defaultFileFilters: AiDataFilters = {
  deals: [],
  investors: [],
  measures: [],
  effectiveDate: null,
  createdAt: null,
  status: null
};

const commonInputProps: Pick<TextFieldProps, "sx"> = {
  sx: { ".MuiInputBase-root": { borderRadius: "8px" } }
};

interface Props {
  onClose: () => void;
  isOpen: boolean;
  filters: AiDataFilters;
  setFilters: (data: Partial<AiDataFilters>) => void;
}

export const FiltersDrawer: React.FC<Props> = ({ isOpen, onClose, filters, setFilters }) => {
  const dateFormat = useDateFormatSelect();
  const investors = useInvestorsSelector();
  const deals = useTaskDealsSelect();
  const measures = useMeasureParametersSelector();

  const investorsOptions = useMemo(() => investors.map(({ id, name }) => ({ id, name })), [investors]);
  const measuresOptions = useMemo(() => measures.map(({ id, name }) => ({ id, name })), [measures]);
  const [enableReinitialize, setEnableReinitialize] = useState<boolean>(true);

  useDidUpdate(() => {
    // Return back reinitilzation after clear all filters
    if (isOpen && !enableReinitialize) {
      setEnableReinitialize(true);
    }
  }, [isOpen]);

  const { ...rest } = filters;

  const close = () => {
    onClose();
  };

  const { values, setFieldValue, handleSubmit, resetForm } = useFormik({
    initialValues: { ...rest },
    onSubmit: (result) => {
      setFilters({ ...result });
      setEnableReinitialize(true);
      close();
    },
    enableReinitialize: enableReinitialize
  });

  const clearForm = useCallback(() => {
    setEnableReinitialize(false);
    const { ...rest } = defaultFileFilters;
    resetForm({ values: rest });
  }, []);

  return (
    <Drawer
      sx={{
        form: {
          minWidth: 430,
          maxWidth: 430,
          height: "100%",
          display: "flex",
          flexDirection: "column"
        },
        ".MuiPaper-root": { borderRadius: "0 20px 20px 0" }
      }}
      open={isOpen}
      onClose={close}
    >
      <form onSubmit={handleSubmit}>
        <SimpleHead onIconClick={close}>Filter</SimpleHead>
        <Stack
          sx={{
            flexGrow: 1,
            gap: "24px",
            overflowY: "auto",
            p: "18px 30px"
          }}
        >
          <Autocomplete
            disableCloseOnSelect
            value={values.deals}
            onChange={(_, option) => setFieldValue("deals", option)}
            options={deals}
            multiple
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: DealBase) => (
              <li {...props} key={option.id}>
                {option.title}
              </li>
            )}
            renderInput={(params) => <TextField {...params} {...commonInputProps} label="Deal" />}
            getOptionLabel={(option) => option.title}
          />
          <Autocomplete
            disableCloseOnSelect
            options={investorsOptions}
            value={values.investors}
            getOptionLabel={(option) => option.name}
            multiple
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: Pick<IInvestor, "id" | "name">) => (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )}
            onChange={(_, option) => setFieldValue("investors", option)}
            renderInput={(params) => <TextField {...params} {...commonInputProps} label="Investor" />}
          />

          <Autocomplete
            disableCloseOnSelect
            options={measuresOptions}
            value={values.measures}
            getOptionLabel={(option) => option.name}
            multiple
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: { id: number; name: string }) => (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )}
            onChange={(_, option) => setFieldValue("measures", option)}
            renderInput={(params) => <TextField {...params} {...commonInputProps} label="Measure" />}
          />
          <Autocomplete
            disableCloseOnSelect
            options={["New", "Accepted", "Declined"]}
            value={values.status || []}
            multiple
            getOptionLabel={(option) => option}
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: string) => (
              <li {...props} key={option}>
                {option}
              </li>
            )}
            onChange={(_, option) => setFieldValue("status", option)}
            renderInput={(params) => <TextField {...params} {...commonInputProps} label="Status" />}
          />

          <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", gap: "16px" }}>
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              value={values.createdAt?.from ? dayjs(values.createdAt.from) : null}
              label="Created At Date From"
              onChange={(date) => {
                if (date && date.isValid()) {
                  setFieldValue("createdAt", {
                    ...values.createdAt,
                    from: date.format("YYYY-MM-DD")
                  });
                }
              }}
              slotProps={{
                textField: { placeholder: "Select" },
                inputAdornment: { position: "start" },
                popper: {
                  sx: {
                    ".MuiPickersCalendarHeader-labelContainer": {
                      fontSize: "0.9rem"
                    }
                  }
                }
              }}
            />
            -
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              value={values.createdAt?.to ? dayjs(values.createdAt.to) : null}
              label="To"
              onChange={(date) => {
                if (date && date.isValid()) {
                  setFieldValue("createdAt", {
                    ...values.createdAt,
                    to: date.format("YYYY-MM-DD")
                  });
                }
              }}
              slotProps={{
                textField: { placeholder: "Select" },
                inputAdornment: { position: "start" },
                popper: {
                  sx: {
                    ".MuiPickersCalendarHeader-labelContainer": {
                      fontSize: "0.9rem"
                    }
                  }
                }
              }}
            />
          </Stack>

          <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", gap: "16px" }}>
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              value={values.effectiveDate?.from ? dayjs(values.effectiveDate.from) : null}
              label="Effective Date From"
              onChange={(date) => {
                if (date && date.isValid()) {
                  setFieldValue("effectiveDate", {
                    ...values.effectiveDate,
                    from: date.format("YYYY-MM-DD")
                  });
                }
              }}
              slotProps={{
                textField: { placeholder: "Select" },
                inputAdornment: { position: "start" },
                popper: {
                  sx: {
                    ".MuiPickersCalendarHeader-labelContainer": {
                      fontSize: "0.9rem"
                    }
                  }
                }
              }}
            />
            -
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              value={values.effectiveDate?.to ? dayjs(values.effectiveDate.to) : null}
              label="To"
              onChange={(date) => {
                if (date && date.isValid()) {
                  setFieldValue("effectiveDate", {
                    ...values.effectiveDate,
                    to: date.format("YYYY-MM-DD")
                  });
                }
              }}
              slotProps={{
                textField: { placeholder: "Select" },
                inputAdornment: { position: "start" },
                popper: {
                  sx: {
                    ".MuiPickersCalendarHeader-labelContainer": {
                      fontSize: "0.9rem"
                    }
                  }
                }
              }}
            />
          </Stack>
        </Stack>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 -1px 5px 1px rgba(0,0,0,.08)",
            p: "18px 30px"
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              textDecoration: "underline",
              fontWeight: 500,
              cursor: "pointer"
            }}
            onClick={clearForm}
          >
            Clear All Filters
          </Typography>
          <Stack direction="row" gap="18px">
            <Button variant="outlined" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              Apply
            </Button>
          </Stack>
        </Stack>
      </form>
    </Drawer>
  );
};
